import * as zod from 'zod';

import type { FeatureFlagKey } from '@sb/feathers-types';
import { BlendConfig, ZERO_BLEND_CONFIG } from '@sb/motion-planning';
import {
  ActionRequiredError,
  MotionSpeed,
  SpeedLimitOption,
  Step,
} from '@sb/remote-control/types';
import { calculateSpeedProfileFromStepMotionSpeed } from '@sb/remote-control/util/calculateSpeedProfileFromStepMotionSpeed';
import { countStepKinds } from '@sb/remote-control/util/countStepKinds';
import { listAllSteps } from '@sb/remote-control/util/listAllSteps';
import { validateMotionSpeed } from '@sb/remote-control/util/validators';
import type { StepKind } from '@sb/routine-runner';
import { type SpeedProfile } from '@sb/routine-runner';
import { getPermittedChildStepKindsShared } from '@sb/routine-validation/step-rules/moveArmToV2';

/**
 * Types matching the routine runner schema for "MoveArmTo" steps.
 */
export namespace MoveArmToTargetStepV2 {
  export const name = 'Move arm';
  export const description =
    'Specify a location that the robot should go to by defining the joint or gripper positions';
  export const librarySection = Step.LibrarySection.Basic;
  export const librarySort = '1';
  export const featureFlag = 'ros2';
  export const argumentKind = 'MoveArmToV2';

  export const isDecorator = true;

  export function getPermittedChildStepKinds(
    featureFlags: Partial<Record<FeatureFlagKey, boolean>> = {},
  ): readonly StepKind[] {
    return getPermittedChildStepKindsShared(featureFlags);
  }

  export const alwaysShowDecoratorStepPlaceholder = true;

  export const Arguments = zod.object({
    argumentKind: zod.literal('MoveArmToV2'),
    motionSpeed: zod.preprocess(
      (val) => (val == null ? undefined : val),
      MotionSpeed.optional(),
    ),
    blendConfig: BlendConfig.optional(),
    speedLimitOption: SpeedLimitOption.exclude(['PARENT_DEFAULTS']).default(
      'NO_MOTION_LIMITS',
    ),
    moveDynamicBaseToReachPosition: zod.boolean().optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({
    step,
    stepConfiguration,
    featureFlags,
  }) => {
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    const permittedSteps = getPermittedChildStepKinds(featureFlags ?? {});

    if (!permittedSteps) {
      return;
    }

    if (
      !step.steps.every((nestedStep) =>
        permittedSteps.includes(nestedStep.stepKind),
      )
    ) {
      const badStep = step.steps.find(
        (nestedStep) => !permittedSteps.includes(nestedStep.stepKind),
      );

      if (badStep == null) {
        // Should never happen
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Move Arm steps must contain only valid child steps.',
        });
      }

      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `Move Arm steps may not contain ${badStep.name} steps.`,
      });
    }
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    baseSpeedProfile,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const { motionSpeed, blendConfig, moveDynamicBaseToReachPosition } = args;

    validateMotionSpeed('Move Arm', motionSpeed);

    const speedProfile: SpeedProfile = calculateSpeedProfileFromStepMotionSpeed(
      {
        motionSpeed,
        baseSpeedProfile,
      },
    );

    return {
      ...stepData,
      stepKind: 'MoveArmToV2',
      args: {
        speedProfile,
        blendConfig: blendConfig || ZERO_BLEND_CONFIG,
        moveDynamicBaseToReachPosition,
      },
    };
  };

  /**
   * Step description for the move arm step is "Move arm through X points"
   */
  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration,
    routine,
    includeStepName,
  }) => {
    if (stepConfiguration?.stepKind !== argumentKind) return null;
    if (routine == null) return null;

    // Find the current step with all of its substeps
    const allSteps = listAllSteps(routine?.steps || []);
    const step = allSteps.find((s) => s.id === stepConfiguration.id);
    if (step === undefined) return null;

    const targetPoints = countStepKinds(
      step,
      getPermittedChildStepKinds({}) as StepKind[],
    );

    const msg = [
      includeStepName ? 'Move arm' : false,
      `through ${targetPoints}`,
      `segment${targetPoints === 1 ? '' : 's'}`,
    ]
      .filter((s) => s != null && s !== false)
      .join(' ');

    return msg;
  };
}

MoveArmToTargetStepV2 satisfies Step.StepKindInfo;
